import React, { useState, useEffect } from 'react';

import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Table, Input, Tooltip, DatePicker, Empty, ConfigProvider, Select, notification } from 'antd';
import { LoadingOutlined, ReloadOutlined, HistoryOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';

import Filter from '@controls/filter/filter';
import Toolbar from '@controls/toolbar/toolbar';

import { exception } from '@extensions/notification';
import { getDateTimeLocal, getEnumList } from '@extensions/utils';

import { userLoaded, setFilter } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { serverFetch } from '@flp/server';

import { IAuditLog } from '@entities/audit-log';
import { IAuditLogFilter } from '@entities/audit-log-filter';

import { AuditLogReferenceType, enumLabel as referenceTypeLabel } from '@enums/audit-log-reference-type';
import { IEnumItem } from '@enums/enum-item';

import { ImportTruckIcon, DeliveryTruckIcon, BillIcon, BoxesIcon, CartIcon, MovingIcon } from '@icons/index';

dayjs.extend(utc);

const filterContext: string = 'AuditLogs';

const AuditLogs = () => {
    const { RangePicker } = DatePicker;

    const d = useAppDispatch();
    const [api, contextHolder] = notification.useNotification();

    const filter = useAppSelector<IAuditLogFilter>((s) => s.filters[filterContext]);

    const [logs, setLogs] = useState<Array<IAuditLog>>();
    const [loading, setLoading] = useState(false);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [showFilter, setShowFilter] = useState<boolean>(true);

    const [referenceTypes] = useState<Array<IEnumItem>>(getEnumList(AuditLogReferenceType, referenceTypeLabel));

    useEffect(() => {
        let cleanup = false;

        if (!refreshRequired) return;

        const fetchData = async () => {
            setLoading(true);

            const promises = [
                await serverFetch('auditlogs', { method: 'GET', queryParams: filter })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения логов', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setLogs(result[0][0]);

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <Filter
                display={showFilter}
                items={[
                    <Select
                        key='referenceType'
                        placeholder='Тип'
                        value={filter?.referenceTypes}
                        size='middle'
                        mode='multiple'
                        maxTagCount='responsive'
                        allowClear
                        style={{ width: 300 }}
                        onChange={(value) => d(setFilter({ ...filter, referenceTypes: value }, filterContext))}
                        options={referenceTypes.map((s) => {
                            return { value: s.value, label: s.label };
                        })}
                    />,
                    <Input
                        style={{ width: 150 }}
                        key='referenceNumber'
                        placeholder='Номер'
                        value={filter?.referenceNumber}
                        onChange={(data) => {
                            d(setFilter({ ...filter, referenceNumber: data.target.value }, filterContext));
                        }}
                    />,
                    <Input
                        style={{ width: 250 }}
                        key='searchText'
                        placeholder='Действие...'
                        value={filter?.searchText}
                        onChange={(data) => {
                            d(setFilter({ ...filter, searchText: data.target.value }, filterContext));
                        }}
                    />,
                    <RangePicker
                        style={{ width: 200 }}
                        allowEmpty={[true, true]}
                        key='createdOn'
                        format='DD.MM.YYYY'
                        placeholder={['Период с', 'по']}
                        cellRender={(current) => {
                            return <div className='ant-picker-cell-inner'>{(current as Dayjs).date()}</div>;
                        }}
                        value={[
                            filter?.createdFrom ? dayjs(filter?.createdFrom) : null,
                            filter?.createdTo ? dayjs(filter?.createdTo) : null,
                        ]}
                        onChange={(value) => {
                            if (!value) {
                                d(
                                    setFilter(
                                        {
                                            ...filter,
                                            createdFrom: undefined,
                                            createdTo: undefined,
                                        },
                                        filterContext
                                    )
                                );
                                return;
                            }

                            d(
                                setFilter(
                                    {
                                        ...filter,
                                        createdFrom: value[0]
                                            ? dayjs(value[0]).utc(true).startOf('day').toString()
                                            : undefined,
                                        createdTo: value[1]
                                            ? dayjs(value[1]).utc(true).endOf('day').toString()
                                            : undefined,
                                    },
                                    filterContext
                                )
                            );
                        }}
                    />,
                ]}
                onClear={() => d(setFilter({}, filterContext))}
            />
        );
    };

    const onRenderEmpty = () => <Empty description='Укажите критерии поиска...' />;

    const renderTable = () => {
        const columns: ColumnsType<IAuditLog> = [
            {
                title: '',
                width: 50,
                align: 'center',
                render: (_, record) => {
                    switch (record.referenceType) {
                        case AuditLogReferenceType.Truck:
                            return (
                                <Tooltip placement='top' title='Машина'>
                                    <div>
                                        <ImportTruckIcon />
                                    </div>
                                </Tooltip>
                            );
                        case AuditLogReferenceType.Shipment:
                            return (
                                <Tooltip placement='top' title='Доставка'>
                                    <div>
                                        <DeliveryTruckIcon />
                                    </div>
                                </Tooltip>
                            );
                        case AuditLogReferenceType.Bill:
                            return (
                                <Tooltip placement='top' title='Счет'>
                                    <div>
                                        <BillIcon />
                                    </div>
                                </Tooltip>
                            );
                        case AuditLogReferenceType.BoxGroup:
                            return (
                                <Tooltip placement='top' title='Партия'>
                                    <div>
                                        <BoxesIcon />
                                    </div>
                                </Tooltip>
                            );
                        case AuditLogReferenceType.AdditionalLoad:
                            return (
                                <Tooltip placement='top' title='Догруз'>
                                    <div>
                                        <CartIcon />
                                    </div>
                                </Tooltip>
                            );
                        case AuditLogReferenceType.TransferBox:
                            return (
                                <Tooltip placement='top' title='Перестановка'>
                                    <div>
                                        <MovingIcon />
                                    </div>
                                </Tooltip>
                            );
                        case AuditLogReferenceType.Transaction:
                            return (
                                <Tooltip placement='top' title='Платежная операция'>
                                    <div>
                                        <HistoryOutlined />
                                    </div>
                                </Tooltip>
                            );
                        default:
                            return '';
                    }
                },
            },
            {
                title: 'Номер',
                width: 150,
                align: 'center',
                dataIndex: 'referenceNumber',
            },

            {
                title: 'Действие',
                dataIndex: 'action',
                width: 850,
            },
            {
                title: 'Инициатор',
                dataIndex: 'createdOnName',
                width: 250,
            },
            {
                title: 'Дата / Время',
                width: 150,
                render: (_, record) => {
                    return getDateTimeLocal(record.createdOn);
                },
            },
            {},
        ];

        return (
            <ConfigProvider renderEmpty={!filter ? onRenderEmpty : undefined}>
                <Table
                    rowKey='id'
                    size='small'
                    columns={columns}
                    dataSource={logs}
                    loading={{
                        spinning: loading,
                        indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                    }}
                    pagination={false}
                    scroll={{ y: `calc(100vh - ${showFilter ? 240 : 182}px)`, x: 'max-content' }}
                />
            </ConfigProvider>
        );
    };

    return (
        <>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {contextHolder}
        </>
    );
};

export default AuditLogs;
